import { useEffect, useMemo, useRef, useState } from 'react'
import { CheckCircle2 } from 'lucide-react'
import confetti from 'canvas-confetti'
import { cn, formatTimeHuman } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type { BusinessDay } from '@/types'

interface ClosingStreakCardProps {
  history: BusinessDay[] | undefined
  streak: number
}

const WINDOW_OPTIONS = [7, 14, 30] as const
type WindowLength = (typeof WINDOW_OPTIONS)[number]
const WINDOW_STORAGE_KEY = 'daily_closing_streak_window'

/**
 * Habit-tracker row of check icons — one per calendar day, ending with
 * today on the right. Filled emerald when that day's
 * business_days.status === 'closed', gray outline otherwise. Today's
 * check pulses softly when pending.
 */
export function ClosingStreakCard({ history, streak }: ClosingStreakCardProps) {
  const [windowDays, setWindowDays] = useState<WindowLength>(() => {
    if (typeof window === 'undefined') return 7
    const raw = window.localStorage.getItem(WINDOW_STORAGE_KEY)
    const n = Number(raw)
    return (WINDOW_OPTIONS as readonly number[]).includes(n)
      ? (n as WindowLength)
      : 7
  })

  const days = useMemo(
    () => buildLastNDays(history ?? [], windowDays),
    [history, windowDays],
  )

  const todayCell = days[days.length - 1]
  useConfettiOnTodayClose(todayCell)

  const handleWindowChange = (next: WindowLength) => {
    setWindowDays(next)
    try {
      window.localStorage.setItem(WINDOW_STORAGE_KEY, String(next))
    } catch {
      // localStorage may be unavailable in private browsing — silently ignore
    }
  }

  const checkSize =
    windowDays === 30 ? 'h-5 w-5 sm:h-6 sm:w-6' : 'h-7 w-7 sm:h-8 sm:w-8'
  const labelSize = windowDays === 30 ? 'text-[9px]' : 'text-[10px]'
  const rowLayout =
    windowDays === 7
      ? 'flex items-end gap-3 sm:gap-4'
      : 'flex items-end justify-between gap-1'

  return (
    <TooltipProvider delayDuration={150}>
      <div className="rounded-2xl border border-border bg-card p-4 sm:px-5 sm:py-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <StreakLabel streak={streak} />
          <WindowToggle value={windowDays} onChange={handleWindowChange} />
        </div>
        {/* 14/30-day rows are wider than a phone — scroll inside the card,
            anchored to the end so "today" is what you see first. */}
        <div dir="rtl" className="no-scrollbar overflow-x-auto">
          <div dir="ltr" className={cn(rowLayout, 'w-max min-w-full [&>*]:shrink-0')}>
            {days.map((d) => (
              <DayCheckCell key={d.iso} day={d} sizeClass={checkSize} labelClass={labelSize} />
            ))}
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}

interface DayCell {
  iso: string
  dow: string
  date: number
  closed: boolean
  isToday: boolean
  pending: boolean
  closedAt?: string | null
}

function DayCheckCell({
  day,
  sizeClass,
  labelClass,
}: {
  day: DayCell
  sizeClass: string
  labelClass: string
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex cursor-default flex-col items-center gap-1">
          <CheckCircle2
            className={cn(
              sizeClass,
              'transition-colors',
              day.closed
                ? 'text-emerald-500 drop-shadow-sm'
                : 'text-zinc-300 dark:text-foreground',
              day.pending && 'animate-pulse text-amber-400 dark:text-amber-600',
            )}
            fill={day.closed ? 'currentColor' : 'none'}
            stroke={day.closed ? 'white' : 'currentColor'}
            strokeWidth={day.closed ? 2.5 : 2}
            aria-hidden="true"
          />
          <span
            className={cn(
              labelClass,
              'font-semibold uppercase tracking-wide',
              day.isToday ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {day.dow}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top">
        <div className="font-semibold">{formatHumanDate(day.iso, day.isToday)}</div>
        <div className="mt-0.5 text-muted-foreground">{checkTooltipStatus(day)}</div>
      </TooltipContent>
    </Tooltip>
  )
}

function WindowToggle({
  value,
  onChange,
}: {
  value: WindowLength
  onChange: (next: WindowLength) => void
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Streak window length"
      className="inline-flex items-center rounded-lg border border-border bg-muted/40 p-0.5 text-xs font-medium"
    >
      {WINDOW_OPTIONS.map((opt) => (
        <button
          key={opt}
          type="button"
          role="radio"
          aria-checked={value === opt}
          onClick={() => onChange(opt)}
          className={cn(
            'rounded-md px-2 py-1 transition-colors',
            value === opt
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {opt}d
        </button>
      ))}
    </div>
  )
}

function StreakLabel({ streak }: { streak: number }) {
  if (streak < 1) {
    return (
      <span className="text-sm font-medium text-muted-foreground">
        Daily Closing streak — start today
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
      <CheckCircle2
        className="h-4 w-4 text-emerald-500"
        fill="currentColor"
        stroke="white"
        strokeWidth={2.5}
        aria-hidden="true"
      />
      {streak}-day streak
    </span>
  )
}

function useConfettiOnTodayClose(today: DayCell | undefined) {
  const wasClosed = useRef<boolean | null>(null)
  useEffect(() => {
    if (!today) return
    if (wasClosed.current === null) {
      wasClosed.current = today.closed
      return
    }
    if (!wasClosed.current && today.closed) {
      try {
        confetti({
          particleCount: 90,
          spread: 75,
          origin: { x: 0.5, y: 0.3 },
          colors: ['#10b981', '#059669', '#34d399', '#22c55e', '#a7f3d0'],
          scalar: 1.1,
        })
      } catch {
        // canvas-confetti may fail in headless or restricted contexts —
        // celebration is best-effort, never block the close flow.
      }
    }
    wasClosed.current = today.closed
  }, [today?.closed])
}

/**
 * Builds N day cells ending with today on the right. Each cell carries
 * its calendar date, day-of-week initial, and whether the matching
 * business_days row is closed.
 */
function buildLastNDays(history: BusinessDay[], n: number): DayCell[] {
  const byDate = new Map<string, BusinessDay>()
  for (const d of history) {
    byDate.set(d.business_date, d)
  }

  const out: DayCell[] = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  for (let offset = n - 1; offset >= 0; offset--) {
    const d = new Date(today)
    d.setDate(d.getDate() - offset)
    const iso = isoDate(d)
    const row = byDate.get(iso)
    const isToday = offset === 0
    const closed = row?.status === 'closed'
    const pending = isToday && !closed && row != null
    out.push({
      iso,
      dow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'][d.getDay()],
      date: d.getDate(),
      closed,
      isToday,
      pending,
      closedAt: row?.closed_at,
    })
  }
  return out
}

function isoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}

function formatHumanDate(iso: string, isToday: boolean): string {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  const weekday = date.toLocaleDateString(undefined, { weekday: 'short' })
  const monthDay = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return isToday ? `Today · ${weekday}, ${monthDay}` : `${weekday}, ${monthDay}`
}

function checkTooltipStatus(day: DayCell): string {
  if (day.closed) {
    if (day.closedAt) {
      const t = new Date(day.closedAt)
      if (!isNaN(t.getTime())) {
        const time = formatTimeHuman(t)
        return `Closed at ${time}`
      }
    }
    return 'Closed'
  }
  if (day.pending) return 'Open — close the day to check it off'
  if (day.isToday) return 'Not started yet'
  return 'Not closed'
}
